import { useRef, useState, useEffect, useCallback } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, useMotionValue, animate } from "framer-motion";
import bs1 from "../../assets/bs1.jpg";
import bs2 from "../../assets/bs2.jpg";
import bs3 from "../../assets/bs3.jpg";
import bs4 from "../../assets/bs4.jpg";
import bs5 from "../../assets/bs5.jpg";
import bs6 from "../../assets/bs6.jpg";
import bs7 from "../../assets/bs7.jpg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=Jost:wght@200;300;400;500&display=swap');
  * { box-sizing: border-box; }
`;

const PRINTS = [
  {
    id: 1,
    image: bs1,
    title: "Alpine Silence",
  },
  {
    id: 2,
    image: bs2,
    title: "Summit Light",
  },
  {
    id: 3,
    image: bs3,
    title: "Ocean Dusk",
  },
  {
    id: 4,
    image: bs4,
    title: "Forest Veil",
  },
  {
    id: 5,
    image: bs5,
    title: "Mirror Lake",
  },
  {
    id: 6,
    image: bs6,
    title: "Dawn Valley",
  },
  {
    id: 7,
    image: bs7,
    title: "Golden Hour",
  },
];

/* ── Keyframes ── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Wrapper ── */
const Section = styled.section`
  background: #0a0804;
  padding: 7rem 0 6rem;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 0 5vw 4rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  animation: ${fadeUp} 0.8s ease both;
`;

const HeaderLeft = styled.div``;

const Eyebrow = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 0.8rem;
`;

const Title = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: #f5ede0;
  margin: 0;
  line-height: 1.1;
  letter-spacing: -0.01em;

  em {
    font-style: italic;
    color: #c9a96e;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 0.6rem;

  @media (max-width: 500px) {
    display: none;
  }
`;

const NavBtn = styled.button`
  width: 2.8rem;
  height: 2.8rem;
  border: 1px solid rgba(245, 237, 224, 0.15);
  background: transparent;
  color: rgba(245, 237, 224, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.3s,
    color 0.3s,
    background 0.3s;

  &:hover {
    border-color: #c9a96e;
    color: #c9a96e;
    background: rgba(201, 169, 110, 0.06);
  }
`;

/* ── Track ── */
const TrackWrapper = styled.div`
  position: relative;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;

const Track = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  padding: 0 5vw;
  will-change: transform;
  user-select: none;
`;

/* ── Card ── */
const Card = styled(motion.div)`
  flex-shrink: 0;
  width: auto;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover img {
    transform: scale(1.04);
  }
`;

const CardImage = styled.img`
  width: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
  display: block;
  transition: transform 0.7s ease;
  pointer-events: none;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 5, 2, 0.75) 0%,
    rgba(8, 5, 2, 0) 50%
  );
  pointer-events: none;
`;

const CardPrice = styled.div`
  position: absolute;
  bottom: 1.4rem;
  left: 1.4rem;
  right: 1.4rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const PriceLabel = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 0.08em;
  color: rgba(245, 237, 224, 0.65);
`;

const PriceValue = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-weight: 400;
  font-size: 1.25rem;
  color: #f5ede0;
  letter-spacing: 0.02em;
`;

/* ── Progress bar ── */
const ProgressWrap = styled.div`
  margin: 3rem 5vw 0;
  height: 1px;
  background: rgba(245, 237, 224, 0.08);
  position: relative;
`;

const ProgressFill = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #c9a96e;
  transform-origin: left;
`;

/* ── Component ── */
export default function BestSelling() {
  const trackRef = useRef(null);
  const x = useMotionValue(0);
  const [progress, setProgress] = useState(0);

  const getConstraints = useCallback(() => {
    const track = trackRef.current;
    if (!track) return { left: 0, right: 0 };
    const trackW = track.scrollWidth;
    const wrapW = track.parentElement?.offsetWidth || window.innerWidth;
    return { left: -(trackW - wrapW), right: 0 };
  }, []);

  /* Update progress bar on drag */
  useEffect(() => {
    return x.on("change", (v) => {
      const { left } = getConstraints();
      if (left === 0) return;
      const pct = Math.min(1, Math.max(0, v / left));
      setProgress(pct);
    });
  }, [x, getConstraints]);

  const slide = (dir) => {
    const STEP = window.innerWidth * 0.35;
    const { left, right } = getConstraints();
    const current = x.get();
    const next = Math.min(right, Math.max(left, current - dir * STEP));
    animate(x, next, { type: "spring", stiffness: 300, damping: 40 });
  };

  return (
    <>
      <GlobalStyle />
      <Section>
        <Header>
          <HeaderLeft>
            <Eyebrow>Most Loved</Eyebrow>
            <Title>
              Best Selling <em>Prints</em>
            </Title>
          </HeaderLeft>

          <NavButtons>
            <NavBtn onClick={() => slide(-1)} aria-label="Previous">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path
                  d="M13 5H1M5 1L1 5l4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NavBtn>
            <NavBtn onClick={() => slide(1)} aria-label="Next">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path
                  d="M1 5h12M9 1l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NavBtn>
          </NavButtons>
        </Header>

        <TrackWrapper>
          <Track
            ref={trackRef}
            style={{ x }}
            drag="x"
            dragConstraints={getConstraints()}
            dragTransition={{ bounceStiffness: 300, bounceDamping: 40 }}
            dragElastic={0.08}
            onDrag={() => {
              const { left } = getConstraints();
              if (left === 0) return;
              setProgress(Math.min(1, Math.max(0, x.get() / left)));
            }}
          >
            {PRINTS.map((print, i) => (
              <Card
                key={print.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: "easeOut" }}
                whileHover={{ y: -6 }}
              >
                <CardImage
                  src={print.image}
                  alt={print.title}
                  draggable={false}
                />
                <CardOverlay />
                <CardPrice>
                  <PriceLabel>Starting from</PriceLabel>
                  <PriceValue>€29.99</PriceValue>
                </CardPrice>
              </Card>
            ))}
          </Track>
        </TrackWrapper>

        {/* Progress bar */}
        <ProgressWrap>
          <ProgressFill style={{ scaleX: progress, width: "100%" }} />
        </ProgressWrap>
      </Section>
    </>
  );
}
